import { BaseApiService } from './BaseApiService';
import { ExerciseDefinition, ExerciseType, MovementPattern, MuscleGroup } from '../../types';
import { CacheEntry, CacheManager } from '../cache/CacheManager';

// Cache storage
const exerciseCache = new Map<string, CacheEntry<ExerciseDefinition[]>>();
const exerciseByIdCache = new Map<string, CacheEntry<ExerciseDefinition>>();
const muscleGroupsCache = new Map<string, CacheEntry<MuscleGroup[]>>();
const exerciseTypesCache = new Map<string, CacheEntry<ExerciseType[]>>();
const categoriesCache = new Map<string, CacheEntry<string[]>>();

/**
 * TODO.md I2: one row of GET /exercises/:id/progress. Mirrors WorkoutManager.getExerciseProgress in
 * the backend - strength exercises carry `maxWeight`/`unit`, stretching/mobility exercises carry
 * `maxHoldTimeSeconds` instead (never both, no meaningless `weight: 0` for hold-time exercises).
 */
export type ExerciseProgressPoint =
  | { date: string; exerciseType: string; maxWeight: number; unit: string }
  | { date: string; exerciseType: string; maxHoldTimeSeconds: number };

/**
 * Exercise Service
 * Handles all exercise-related API operations with caching
 */
export class ExerciseService extends BaseApiService {
  private getLocaleKey(): string {
    try {
      return (localStorage.getItem('gym_planner_locale') || 'en').toLowerCase();
    } catch {
      return 'en';
    }
  }

  /**
   * GET /api/exercises - Fetch all exercises with caching
   * @param filters - Optional filters for muscle group, exercise type, category and search
   */
  async getExercises(filters?: { muscleGroup?: MuscleGroup; type?: ExerciseType; category?: string; search?: string }): Promise<ExerciseDefinition[]> {
    // Create cache key based on filters
    const cacheKey = this.createCacheKey(filters);

    const cachedExercises = CacheManager.get(exerciseCache, cacheKey);
    if (cachedExercises !== null) {
      console.log(`📦 Using cached exercises for key: ${cacheKey}`);

      if (filters?.search) {
        const searchTerm = filters.search.toLowerCase();
        return cachedExercises.filter(ex =>
          ex.localizedName?.toLowerCase().includes(searchTerm) ||
          ex.name.toLowerCase().includes(searchTerm) ||
          ex.localizedDescription?.toLowerCase().includes(searchTerm) ||
          ex.description?.toLowerCase().includes(searchTerm) ||
          ex.muscleGroup.toLowerCase().includes(searchTerm) ||
          ex.equipment?.toLowerCase().includes(searchTerm)
        );
      }

      return cachedExercises;
    }

    // Cache miss or expired, fetch from API
    console.log(`🌐 Fetching fresh exercises for key: ${cacheKey}`);
    let endpoint = '/exercises';

    if (filters) {
      const params = new URLSearchParams();
      if (filters.muscleGroup) params.append('muscleGroup', filters.muscleGroup);
      if (filters.type) params.append('type', filters.type);
      if (filters.category) params.append('category', filters.category);
      // Don't include search in API call - we'll filter cached data

      const queryString = params.toString();
      if (queryString) endpoint += `?${queryString}`;
    }

    const result = await this.get<ExerciseDefinition[]>(endpoint);
    const exercises = result || [];

    // Cache the result (without search filter)
    const baseCacheKey = this.createCacheKey({ muscleGroup: filters?.muscleGroup, type: filters?.type, category: filters?.category });
    CacheManager.set(exerciseCache, baseCacheKey, exercises, CacheManager.ttl.exercises);

    // If we have search filter, apply it
    if (filters?.search) {
      const searchTerm = filters.search.toLowerCase();
      return exercises.filter(ex => 
        ex.localizedName?.toLowerCase().includes(searchTerm) ||
        ex.name.toLowerCase().includes(searchTerm) ||
        ex.localizedDescription?.toLowerCase().includes(searchTerm) ||
        ex.description?.toLowerCase().includes(searchTerm) ||
        ex.muscleGroup.toLowerCase().includes(searchTerm) ||
        ex.equipment?.toLowerCase().includes(searchTerm)
      );
    }
    
    return exercises;
  }

  /**
   * GET /api/exercises/:id - Get exercise by ID with caching
   *
   * Consolidated cache (TODO.md F2): this used to only benefit from whatever happened to already be
   * in the "get all" cache entry, falling through to an *uncached* network call otherwise - callers
   * that only ever look up individual exercises by id (e.g. hydrating a template's exercise list)
   * never got a cache hit. `exerciseByIdCache` is now the dedicated cache for that access pattern, so
   * per-id lookups are cached on their own terms instead of piggybacking on the list cache.
   *
   * TODO.md B4 correction: the old version also *served* a by-id lookup straight out of an
   * already-warm "get all" cache entry when one existed, to skip a network call. That shortcut is no
   * longer safe: the backend deliberately strips `notes`/`localizedNotes` from list responses
   * (`withoutNotes` in routes/exercises.ts, AC-EX-20) but includes them on the single-exercise
   * response, so a list-derived entry is an incomplete substitute for a real detail fetch, not just a
   * cache-source optimization. Since `getExercises()` runs unconditionally on `ExercisesPage` mount,
   * the list cache is warm essentially every time a user opens a detail view - the shortcut would
   * silently return `notes: undefined` on the very first lookup, permanently masking the field. Removed
   * rather than special-cased per-field, since any future detail-only field would reintroduce the same
   * bug. `exerciseByIdCache` still caches whatever `getExerciseById` returns, so repeat lookups for the
   * same id remain free.
   */
  async getExerciseById(id: string): Promise<ExerciseDefinition> {
    const cachedById = CacheManager.get(exerciseByIdCache, id);
    if (cachedById !== null) {
      console.log(`📦 Using cached exercise for ID: ${id}`);
      return cachedById;
    }

    // Cache miss, fetch from API
    console.log(`🌐 Fetching fresh exercise for ID: ${id}`);
    const exercise = await this.get<ExerciseDefinition>(`/exercises/${id}`);
    CacheManager.set(exerciseByIdCache, id, exercise, CacheManager.ttl.exercises);
    return exercise;
  }

  /**
   * GET /api/exercises/search?name={name} - Search exercises by name using backend API
   * @param query - Search term to look for
   * @returns Promise<ExerciseDefinition[]> - Matching exercises from backend
   */
  async searchExercises(query: string): Promise<ExerciseDefinition[]> {
    if (!query || query.trim() === '') {
      // If empty search, return all exercises
      return this.getExercises();
    }

    console.log(`🔍 Searching exercises for query: "${query}"`);
    
    // Use backend search endpoint
    const endpoint = `/exercises/search?name=${encodeURIComponent(query.trim())}`;
    const result = await this.get<ExerciseDefinition[]>(endpoint);
    
    console.log(`🔍 Backend search returned ${result?.length || 0} exercises`);
    return result || [];
  }

  /**
   * Find exercise by exact name match (case-insensitive) with caching
   * @param name - Exercise name to search for
   * @returns Matching exercise or undefined if not found
   */
  async findExerciseByName(name: string): Promise<ExerciseDefinition | undefined> {
    try {
      const exercises = await this.getExercises();
      const searchTerm = name.toLowerCase();
      return exercises.find(ex => ex.name.toLowerCase() === searchTerm);
    } catch (error) {
      console.error('Error finding exercise by name:', error);
      return undefined;
    }
  }

  /**
   * POST /api/exercises - Create a new exercise
   * Invalidates cache after creation
   */
  async createExercise(data: {
    name: string;
    muscleGroup: MuscleGroup;
    exerciseType?: ExerciseType;
    movementPattern?: MovementPattern;
    categories?: string[];
    imageUrl?: string;
    description?: string;
    equipment?: string;
    userId?: string;
    isPrivate?: boolean;
  }): Promise<ExerciseDefinition> {
    const result = await this.post<ExerciseDefinition>('/exercises', data);
    
    // Invalidate cache after creating new exercise
    this.invalidateCache();
    
    return result;
  }

  async uploadExerciseImage(id: string, file: File): Promise<ExerciseDefinition> {
    const token = (() => {
      try {
        return localStorage.getItem('gym_planner_token');
      } catch {
        return null;
      }
    })();

    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${BaseApiService.BASE_URL}/exercises/${id}/image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Image upload failed: ${response.status} ${response.statusText}`);
    }

    const updated = (await response.json()) as ExerciseDefinition;
    // invalidateCache() clears exerciseByIdCache too (F2 consolidation), so a newly uploaded image
    // shows up immediately wherever an exercise is looked up by id (e.g. template views) - no
    // separate localStorage cleanup needed anymore.
    this.invalidateCache();

    return updated;
  }

  /**
   * PUT /api/exercises/:id - Update an exercise
   * Invalidates cache after update
   */
  async updateExercise(
    id: string,
    data: {
      name?: string;
      muscleGroup?: MuscleGroup;
      exerciseType?: ExerciseType;
      movementPattern?: MovementPattern;
      categories?: string[];
      imageUrl?: string;
      description?: string;
      // Edit form only (TODO.md B4) - the create form stays minimal by B5's precedent, so
      // createExercise() below deliberately does not get a notes param.
      notes?: string;
      equipment?: string;
      isPrivate?: boolean;
      references?: string[];
    }
  ): Promise<ExerciseDefinition> {
    const result = await this.put<ExerciseDefinition>(`/exercises/${id}`, data);

    this.invalidateCache();

    return result;
  }

  /**
   * DELETE /api/exercises/:id - Delete an exercise
   * Invalidates cache after deletion
   */
  async deleteExercise(id: string): Promise<void> {
    await this.delete<void>(`/exercises/${id}`);
    // invalidateCache() clears exerciseCache and exerciseByIdCache (F2 consolidation) - this service
    // is the single source of truth for exercise caching now, so there is no separate localStorage
    // layer left to clean up here.
    this.invalidateCache();
  }

  /**
   * GET /api/exercises/muscle-groups - Get all available muscle groups with caching
   */
  async getMuscleGroups(): Promise<MuscleGroup[]> {
    // Check cache first
    const cached = CacheManager.get(muscleGroupsCache, 'all');
    if (cached !== null) {
      console.log('📦 Using cached muscle groups');
      return cached;
    }

    // Cache miss, fetch from API
    console.log('🌐 Fetching fresh muscle groups');
    const result = await this.get<MuscleGroup[]>('/exercises/muscle-groups');
    const muscleGroups = result || [];
    
    // Cache the result
    CacheManager.set(muscleGroupsCache, 'all', muscleGroups, CacheManager.ttl.muscleGroups);
    
    return muscleGroups;
  }

  /**
   * GET /api/exercises/types - Get exercise types currently in use, with caching
   */
  async getExerciseTypes(): Promise<ExerciseType[]> {
    const cached = CacheManager.get(exerciseTypesCache, 'all');
    if (cached !== null) {
      console.log('📦 Using cached exercise types');
      return cached;
    }

    console.log('🌐 Fetching fresh exercise types');
    const result = await this.get<ExerciseType[]>('/exercises/types');
    const types = result || [];

    CacheManager.set(exerciseTypesCache, 'all', types, CacheManager.ttl.muscleGroups);

    return types;
  }

  /**
   * GET /api/exercises/categories - Get the full category tag lookup table, with caching
   */
  async getCategories(): Promise<string[]> {
    const cached = CacheManager.get(categoriesCache, 'all');
    if (cached !== null) {
      console.log('📦 Using cached exercise categories');
      return cached;
    }

    console.log('🌐 Fetching fresh exercise categories');
    const result = await this.get<string[]>('/exercises/categories');
    const categories = result || [];

    CacheManager.set(categoriesCache, 'all', categories, CacheManager.ttl.muscleGroups);

    return categories;
  }

  /**
   * Clear exercise cache. Called on logout (AuthContext) so a subsequent login by a different user
   * never sees a previous user's cached private exercises - the cache key is scoped by
   * locale/muscleGroup/type/category, not by user, so it does not self-invalidate across accounts.
   */
  clearCache(): void {
    CacheManager.clear(exerciseCache);
    CacheManager.clear(exerciseByIdCache);
    CacheManager.clear(muscleGroupsCache);
    CacheManager.clear(exerciseTypesCache);
    CacheManager.clear(categoriesCache);
    console.log('🗑️ Exercise cache cleared');
  }

  /**
   * Invalidate cache (called after data modifications)
   */
  private invalidateCache(): void {
    // Clear all exercise-related caches
    CacheManager.clear(exerciseCache);
    CacheManager.clear(exerciseByIdCache);
    console.log('🔄 Exercise cache invalidated');
  }

  /**
   * Create cache key based on filters
   * Includes type/category so filtering by the new axes does not collide with
   * (or get served from) the muscleGroup-only cache entries.
   */
  private createCacheKey(filters?: { muscleGroup?: MuscleGroup; type?: ExerciseType; category?: string; search?: string }): string {
    const locale = this.getLocaleKey();
    const parts = [`locale:${locale}`];
    parts.push(filters?.muscleGroup ? `muscle-group:${filters.muscleGroup}` : 'all');
    if (filters?.type) parts.push(`type:${filters.type}`);
    if (filters?.category) parts.push(`category:${filters.category}`);
    return parts.join(':');
  }

  /**
   * GET /api/exercises/:id/progress - Get exercise progress over the last 3 months. Rows branch on
   * exerciseType (TODO.md I2) - see ExerciseProgressPoint.
   * @param exerciseId - Exercise ID or name
   */
  async getExerciseProgress(exerciseId: string): Promise<ExerciseProgressPoint[]> {
    try {
      const response = await this.get(`/exercises/${exerciseId}/progress`);
      return response as ExerciseProgressPoint[];
    } catch (error) {
      console.error('Error fetching exercise progress:', error);
      throw error;
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { 
    exerciseCacheSize: number; 
    muscleGroupsCacheSize: number;
    exerciseKeys: string[]; 
    muscleGroupKeys: string[];
    totalAge: number 
  } {
    const now = Date.now();
    const exerciseKeys = Array.from(exerciseCache.keys());
    const muscleGroupKeys = Array.from(muscleGroupsCache.keys());
    
    const totalAge = [...exerciseKeys, ...muscleGroupKeys].reduce((sum, key) => {
      const cache = exerciseCache.get(key) || muscleGroupsCache.get(key);
      return sum + (cache ? now - cache.timestamp : 0);
    }, 0);
    
    return {
      exerciseCacheSize: exerciseCache.size,
      muscleGroupsCacheSize: muscleGroupsCache.size,
      exerciseKeys,
      muscleGroupKeys,
      totalAge
    };
  }
}
